import type { AuthService, IExtendedUserDetails } from '@sine/auth-client';

import deviceService from '../authentication/device.service';
import { mobileVerificationStorage } from '../pre-login/helpers/mobileVerificationStorage';
import rxEventHub, { RxEvent } from '../services/events/rx';
import socketEventHub, { ReceiveSocketEvent } from '../services/events/sockets';
import { storage } from '../services/storage/storage.service';
import teamService from '../services/team/team.service';
import { STORE_KEYS, store } from '../store';
import teamScopingService from '../team/teamScoping.service';
import teamSwitcherService, { teamSwitcherInterceptor } from '../team/teamSwitcher.service';

type ResolveFn<T> = (value: T | PromiseLike<T>) => void;
type AuthData = IExtendedUserDetails | undefined;
type PermissionData = Array<string>;

export class AuthHelpers {
  private authService: AuthService;
  private userId: string | null;
  public permissions: PermissionData;

  public authPromise: null | Promise<AuthData>;

  public authResolve: null | ResolveFn<void>;
  public permissionsResolve: null | ResolveFn<void>;

  public authDataDefer: Promise<void>;
  public permissionsDefer: null | Promise<void>;

  constructor(authService: AuthService) {
    this.authService = authService;
    this.userId = null;
    this.authPromise = null;
    this.authResolve = null;
    this.permissionsResolve = null;
    this.permissions = [];
    this.authDataDefer = this.createAuthDataDefer();
    this.permissionsDefer = null;
  }

  createPermissionsDefer() {
    return new Promise<void>((resolve) => {
      this.permissionsResolve = resolve;
    });
  }

  createAuthDataDefer() {
    return new Promise<void>((resolve) => {
      this.authResolve = resolve;
    });
  }

  clearPermissions() {
    this.permissionsResolve = null;
    this.permissionsDefer = null;
    this.permissions = [];
  }

  waitForPermissionsDefer() {
    return this.permissionsDefer;
  }

  waitForAuthDataDefer() {
    return this.authDataDefer;
  }

  async getAuthPromise() {
    if (this.authPromise) {
      await teamScopingService.initializePendoWithMetadata();
      return this.authPromise;
    }

    const { authToken } = this.authService.getCurrentTokens();

    if (!authToken) {
      throw 'NOT_AUTHENTICATED';
    }

    const token = JSON.parse(atob(authToken.split('.')[1]));

    if (token.challengeRequired) {
      return;
    }

    // if reCaptchaToken is in local storage which means the user was logged in WCI first
    if (storage.getItem('reCaptchaToken')) {
      // if yes, clear both device token and authToken and remove reCaptchaToken from local storage
      storage.removeItem('reCaptchaToken');
      deviceService.trashDeviceToken();
      await this.authService.trashUser();
      this.authPromise = null;

      // and generate a new device token for Web
      await deviceService.register();
    }

    // eslint-disable-next-line no-async-promise-executor
    return (this.authPromise = new Promise<AuthData>(async (resolve, reject) => {
      let userProfileData: AuthData;

      try {
        userProfileData = await this.authService.refetchAuthData();
      } catch (err) {
        this.authPromise = null;

        return reject('NOT_AUTHENTICATED');
      }

      await this.refreshPermissions();
      await teamScopingService.initializePendoWithMetadata();
      await socketEventHub.start();

      this.authResolve?.();
      return resolve(userProfileData);
    }));
  }

  // this function is for accepting permissions
  async preloadAuthentication() {
    try {
      await this.checkAuthenticated();
    } catch (err) {
      // if encountered with an error, swallow the error quietly
      //  as user may not be authenticated
    }
  }
  async checkAuthenticated() {
    await deviceService.register();
    const auth = await this.getAuthPromise();

    // HACK: this is to wipe the initial loading screen
    store.updateState({ [STORE_KEYS.WAITING_FOR_FIRST_AUTH]: false });

    return auth;
  }

  async refetchPermissions() {
    if (!this.permissionsDefer) {
      rxEventHub.broadcast(RxEvent.TEAM_PERMISSIONS_REFETCHING, {});
      this.permissionsDefer = this.createPermissionsDefer();
      await this.refreshPermissions();
    }
  }

  async refreshPermissions() {
    const { userDetails } = this.authService.getAuthState();

    const currentTeam = teamSwitcherService.getCurrentTeam();
    if (currentTeam) {
      this.permissions = await teamService.fetchTeamPermissions(currentTeam.id);
    } else {
      this.permissions = userDetails?.permissions || [];
    }
    this.permissionsResolve?.();

    rxEventHub.broadcast(RxEvent.TEAM_PERMISSIONS_REFRESHED, {});

    return this.permissions;
  }

  async refetchAuthData() {
    const data = await this.authService.refetchAuthData();

    this.clearPermissions();
    await this.refetchPermissions();

    return data;
  }

  async reloadTeamSwitcher() {
    await this.refetchAuthData();
    await teamSwitcherService.reinitialise();
    teamSwitcherInterceptor.startIntercepting();
    rxEventHub.broadcast(RxEvent.TEAM_CHANGED, {});
  }

  async initialiseAuthState() {
    await this.authService.initialize();
  }

  initialiseEvents() {
    const refetchAuthData = this.refetchAuthData.bind(this);
    rxEventHub.on(RxEvent.LOGIN, this.initialiseAuthState.bind(this));
    socketEventHub.on(ReceiveSocketEvent.USER_PERMISSIONS_CHANGED, refetchAuthData);
    rxEventHub.on(RxEvent.USER_PERMISSIONS_CHANGED, this.reloadTeamSwitcher.bind(this));
    rxEventHub.on(RxEvent.TRIAL_DATA_LOADING, refetchAuthData);
    rxEventHub.on(RxEvent.USER_DETAILS_UPDATED, refetchAuthData);
    rxEventHub.on(RxEvent.TEAM_CREATED, this.reloadTeamSwitcher.bind(this));

    rxEventHub.on(RxEvent.USER_SOFT_DELETED, () => {
      if (!this.isImpersonating()) {
        rxEventHub.broadcast(RxEvent.LOGOUT, {});
        window.location.href = `/login`;
      }
    });

    rxEventHub.on(RxEvent.LOGOUT, () => {
      mobileVerificationStorage.clearVerifyCodeRequestSent();
      this.authPromise = null;
      this.userId = null;
      this.clearPermissions();
      socketEventHub.stop();
      this.authDataDefer = this.createAuthDataDefer();
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      this.authService.logout();
    });
  }

  hasPermission(permission: string) {
    return !!this.permissions.find((p) => p && p.includes(permission));
  }

  isImpersonating() {
    const { userDetails } = this.authService.getAuthState();
    return userDetails?.administeredBy && userDetails.permissions.includes('SYSTEM_ADMIN');
  }
}
